<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import { allowedNextStatuses, requiresReason } from '@domain/contract/status-transitions';
  import { transitionContract } from '@entities/contract/store.svelte';
  import type { Contract } from '@domain/contract/model';
  import {
    CONTRACT_STATUS_LABELS,
    type ContractStatus,
  } from '@shared/types/contract';

  interface Props {
    open: boolean;
    contract: Contract;
    onClose: () => void;
    onChanged: (contract: Contract) => void;
  }

  let { open, contract, onClose, onChanged }: Props = $props();

  let nextStatus = $state<ContractStatus | ''>('');
  let reason = $state('');
  let submitting = $state(false);
  let serverError = $state<string | null>(null);
  let validationError = $state<string | null>(null);

  let candidates = $derived(allowedNextStatuses(contract.status));

  $effect(() => {
    if (open) {
      nextStatus = '';
      reason = '';
      serverError = null;
      validationError = null;
    }
  });

  function close() {
    onClose();
  }

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    if (nextStatus === '') {
      validationError = '次のステータスを選択してください';
      return;
    }
    if (requiresReason(nextStatus) && reason.trim().length === 0) {
      validationError = '解約理由を入力してください';
      return;
    }
    validationError = null;
    submitting = true;
    serverError = null;
    try {
      const updated = await transitionContract(contract.id, {
        next_status: nextStatus,
        // reason 入力欄は requiresReason(nextStatus) のときだけ表示される
        // (下の {#if} 参照)。表示されていた別ステータス選択時の入力が state に
        // 残っていても、非対象ステータスへは reason を送らない (backend は
        // canceled 以外への reason 供給を validation error で拒否する)。
        reason: requiresReason(nextStatus) ? reason.trim() || null : null,
      });
      onChanged(updated);
    } catch (err) {
      serverError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }
</script>

<Modal {open} title="契約ステータスを変更" onClose={close}>
  <form id="contract-transition-form" onsubmit={handleSubmit}>
    <p class="current">
      現在: <strong>{CONTRACT_STATUS_LABELS[contract.status]}</strong>
    </p>
    {#if candidates.length === 0}
      <p class="muted">このステータスからは遷移できません。</p>
    {:else}
      <label>
        <span>次のステータス <em>*</em></span>
        <select bind:value={nextStatus}>
          <option value="">選択してください</option>
          {#each candidates as s (s)}
            <option value={s}>{CONTRACT_STATUS_LABELS[s]}</option>
          {/each}
        </select>
      </label>
      {#if nextStatus !== '' && requiresReason(nextStatus)}
        <label>
          <span>解約理由 <em>*</em></span>
          <textarea bind:value={reason} rows="2" required></textarea>
        </label>
      {/if}
    {/if}
    {#if validationError}<p class="server-error">{validationError}</p>{/if}
    {#if serverError}<p class="server-error">{serverError}</p>{/if}
  </form>
  {#snippet footer()}
    <button type="button" onclick={close} disabled={submitting}>キャンセル</button>
    <button
      type="submit"
      form="contract-transition-form"
      class="primary"
      disabled={submitting || candidates.length === 0}
    >
      {submitting ? '更新中…' : '更新'}
    </button>
  {/snippet}
</Modal>

<style>
  form { display: flex; flex-direction: column; gap: 12px; }
  label { display: flex; flex-direction: column; gap: 4px; }
  span em { color: #b91c1c; font-style: normal; }
  select, textarea {
    padding: 8px 10px; border: 1px solid #d1d5db; border-radius: 4px;
    font: inherit; background: white;
  }
  textarea { resize: vertical; }
  .server-error { color: #b91c1c; margin: 0; }
  .current { margin: 0; color: #374151; }
  .muted { color: #6b7280; }
  button { padding: 8px 16px; border: 1px solid #d1d5db; background: white; border-radius: 4px; cursor: pointer; font: inherit; }
  button.primary { background: #2563eb; border-color: #2563eb; color: white; }
  button:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
